<template>
	<PCard class="docs-page">
		<template #title>
			<div class="flex align-items-center justify-content-between flex-wrap gap-2">
				<div>
					<WTitle text="Documentation" />
					<WSubTitle text="Project requirements, interface specs, and contract references" />
				</div>
			</div>
		</template>
		<template #content>
			<section class="docs-intro">
				<div class="docs-intro__icon">
					<i class="mdi mdi-book-open-page-variant-outline"></i>
				</div>
				<div>
					<div class="docs-intro__title">Reference library</div>
					<p class="docs-intro__lead">
						Open a document to preview it in the viewer, or download a copy for offline use.
					</p>
				</div>
			</section>

			<div class="docs-grid">
				<button
					:key="item.title"
					class="docs-card"
					type="button"
					v-for="item in list"
					@click="openPdf(item.title, item.pdfUrl)"
				>
					<div
						:class="['docs-card__icon', `docs-card__icon--${item.tone}`]"
					>
						<i :class="item.icon"></i>
					</div>
					<div class="docs-card__body">
						<div class="docs-card__title">{{ item.title }}</div>
						<p class="docs-card__desc">{{ item.description }}</p>
						<span class="docs-card__action">
							View PDF
							<i class="pi pi-arrow-right"></i>
						</span>
					</div>
				</button>
			</div>

			<PDialog
				v-model:visible="showPdfViewer"
				:header="PdfViewerTitle"
				:modal="true"
				:maximizable="true"
				:dismissableMask="true"
				:style="{ width: 'min(1100px, 96vw)' }"
				:contentStyle="{ padding: 0, height: 'min(78vh, 820px)', overflow: 'hidden' }"
				class="docs-pdf-dialog"
			>
				<PdfViewer
					:pdfUrl="pdfUrl"
					:title="PdfViewerTitle"
				/>
			</PDialog>
		</template>
	</PCard>
</template>

<script lang="ts">
	import { defineComponent } from 'vue'
	import PdfViewer from '@/components/common/WPdfViewer.vue'

	export default defineComponent({
		name: 'DocumentationView',
		components: {
			PdfViewer,
		},

		data() {
			return {
				showPdfViewer: false,
				PdfViewerTitle: 'PDF Viewer',
				pdfUrl: '',
				list: [
					{
						title: 'PASS Requirements',
						description: 'Functional and interface requirements for the Power Settlement System.',
						pdfUrl: '/PASS_requirements.pdf',
						icon: 'mdi mdi-clipboard-list-outline',
						tone: 'blue',
					},
					{
						title: 'Interface Specs',
						description: 'External interface definitions for metering, DCS, and data exchange.',
						pdfUrl: '/Interface_Specs.pdf',
						icon: 'mdi mdi-lan-connect',
						tone: 'teal',
					},
					{
						title: 'System Architecture',
						description: 'High-level architecture of PASS services, data flow, and deployment.',
						pdfUrl: '/System_Architecture.pdf',
						icon: 'mdi mdi-sitemap-outline',
						tone: 'amber',
					},
					{
						title: 'Power Purchase Agreement',
						description: 'Contract reference for tariff formulas, deemed energy, and settlement terms.',
						pdfUrl: '/PPA_Power_Purchase_Agreement.pdf',
						icon: 'mdi mdi-file-document-outline',
						tone: 'slate',
					},
				],
			}
		},

		methods: {
			openPdf(title: string, pdfUrl: string) {
				this.pdfUrl = pdfUrl
				this.PdfViewerTitle = title
				this.showPdfViewer = true
			},
		},
	})
</script>

<style scoped>
.docs-intro {
	display: flex;
	align-items: flex-start;
	gap: 0.9rem;
	padding: 1rem 1.15rem;
	margin-bottom: 1.15rem;
	border-radius: 10px;
	border: 1px solid var(--surface-border);
	border-left: 4px solid var(--primary-color);
	background:
		linear-gradient(
			135deg,
			color-mix(in srgb, var(--primary-color) 8%, var(--surface-card)) 0%,
			var(--surface-50, var(--surface-ground)) 55%,
			var(--surface-card) 100%
		);
}

.docs-intro__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 2.4rem;
	height: 2.4rem;
	border-radius: 8px;
	background: color-mix(in srgb, var(--primary-color) 14%, transparent);
	color: var(--primary-color);
	flex-shrink: 0;
}

.docs-intro__icon i {
	font-size: 1.25rem;
}

.docs-intro__title {
	font-size: 1.05rem;
	font-weight: 700;
	line-height: 1.2;
	margin-bottom: 0.25rem;
}

.docs-intro__lead {
	margin: 0;
	font-size: 0.9rem;
	line-height: 1.45;
	color: var(--text-color-secondary);
	max-width: 42rem;
}

.docs-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 0.85rem;
}

.docs-card {
	display: flex;
	align-items: flex-start;
	gap: 0.9rem;
	width: 100%;
	padding: 1.05rem 1.1rem;
	text-align: left;
	cursor: pointer;
	border-radius: 10px;
	border: 1px solid var(--surface-border);
	background: var(--surface-card);
	color: inherit;
	font: inherit;
	transition:
		border-color 0.15s ease,
		transform 0.15s ease,
		box-shadow 0.15s ease;
}

.docs-card:hover {
	border-color: color-mix(in srgb, var(--primary-color) 55%, var(--surface-border));
	box-shadow: 0 8px 22px color-mix(in srgb, var(--primary-color) 10%, transparent);
	transform: translateY(-1px);
}

.docs-card:focus-visible {
	outline: 2px solid var(--primary-color);
	outline-offset: 2px;
}

.docs-card__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 2.5rem;
	height: 2.5rem;
	border-radius: 9px;
	flex-shrink: 0;
}

.docs-card__icon i {
	font-size: 1.25rem;
}

.docs-card__icon--blue {
	background: color-mix(in srgb, var(--blue-500, #3b82f6) 14%, transparent);
	color: var(--blue-600, #2563eb);
}

.docs-card__icon--teal {
	background: color-mix(in srgb, var(--teal-500, #14b8a6) 14%, transparent);
	color: var(--teal-600, #0d9488);
}

.docs-card__icon--amber {
	background: color-mix(in srgb, var(--orange-500, #f59e0b) 16%, transparent);
	color: var(--orange-600, #d97706);
}

.docs-card__icon--slate {
	background: color-mix(in srgb, var(--primary-color) 12%, transparent);
	color: var(--primary-color);
}

.docs-card__body {
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.3rem;
}

.docs-card__title {
	font-size: 1rem;
	font-weight: 650;
	line-height: 1.25;
}

.docs-card__desc {
	margin: 0;
	font-size: 0.86rem;
	line-height: 1.45;
	color: var(--text-color-secondary);
}

.docs-card__action {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	margin-top: 0.35rem;
	font-size: 0.82rem;
	font-weight: 650;
	color: var(--primary-color);
}

.docs-card__action i {
	font-size: 0.7rem;
	transition: transform 0.15s ease;
}

.docs-card:hover .docs-card__action i {
	transform: translateX(2px);
}

@media screen and (max-width: 760px) {
	.docs-grid {
		grid-template-columns: 1fr;
	}
}
</style>

<style>
/* Dialog chrome — unscoped so PrimeVue content fills height */
.docs-pdf-dialog.p-dialog {
	max-height: 96vh;
}

.docs-pdf-dialog .p-dialog-header {
	padding: 0.85rem 1rem;
	border-bottom: 1px solid var(--surface-border);
	background: var(--surface-card);
}

.docs-pdf-dialog .p-dialog-content {
	padding: 0 !important;
	display: flex;
	flex-direction: column;
	background: var(--surface-ground);
}

.docs-pdf-dialog .p-dialog-content > * {
	flex: 1 1 auto;
	min-height: 0;
}
</style>
